import React, { useEffect, useRef, useState, useCallback, useMemo, memo, useLayoutEffect } from 'react';
import {
  Chart,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  TimeScale,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import 'chartjs-adapter-date-fns';

// ============================================================================
// CHART.JS SETUP & PLUGINS
// ============================================================================

Chart.register(
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  TimeScale,
  Tooltip,
  Legend,
  Filler
);

// Watermark Plugin
const watermarkPlugin = {
    id: 'watermark',
    beforeDraw: (chart) => {
        const ctx = chart.canvas.getContext('2d');
    const { width, height, chartArea } = chart;

        ctx.save();
        ctx.globalCompositeOperation = 'destination-over';
    ctx.font = `${width * 0.04}px -apple-system, system-ui, sans-serif`;
    ctx.fillStyle = `rgba(255, 255, 255, 0.08)`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        
    const y = chartArea.bottom - (chartArea.bottom - chartArea.top) * 0.5 - 40;
    ctx.fillText('PARADOX INTELLIGENCE', width / 2, y);
        ctx.restore();
    }
};

Chart.register(watermarkPlugin);

// ============================================================================
// CONSTANTS & CONFIGURATION
// ============================================================================

const COLORS = [
  '#3B82F6', '#00F5FF', '#8B5FFF', '#00FF88', '#FF6B35',
  '#FF1744', '#E91E63', '#00E676', '#FFAB00', '#FFFFFF'
];

const DATA_TYPE_ICONS = {
            search: '/src/assets/icons/google.svg',
            images: '/src/assets/icons/image.svg',
            news: '/src/assets/icons/news.svg',
            youtube: '/src/assets/icons/youtube.svg',
            shopping: '/src/assets/icons/shopping.svg',
            tiktok: '/src/assets/icons/tiktok.svg',
            webtraffic: '/src/assets/icons/webtraffic.svg',
            wikipedia: '/src/assets/icons/wikipedia.svg',
            reddit: '/src/assets/icons/reddit.svg',
            amazon: '/src/assets/icons/amazon.svg',
  price: '/src/assets/icons/price.svg',
  mobile: '/src/assets/icons/mobile.svg',
  ios: '/src/assets/icons/iOSAppstore.svg',
  android: '/src/assets/icons/android.svg',
  custom: '/src/assets/icons/custom.svg',
  excel: '/src/assets/icons/excel.svg'
};

const STYLES = {
  container: {
    background: '#000000',
    borderRadius: '16px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.8)',
    border: '1px solid rgba(255, 255, 255, 0.05)'
  },
  button: {
    base: 'p-2 rounded-lg transition-all duration-200 backdrop-blur-sm',
    icon: 'text-gray-400/60 hover:text-gray-300 hover:bg-white/5',
    active: 'text-white bg-white/10'
  },
  legend: {
    item: 'group flex items-center gap-2 px-3 py-1.5 rounded-lg cursor-pointer transition-all hover:bg-white/5',
    hidden: 'opacity-40'
  }
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

const formatDatasetLabel = (dataType, keyword) => {
  const type = String(dataType || '').trim();
  const term = String(keyword || '').trim();
  if (!type || !term) return term || type || 'Unknown';
  return `${type.charAt(0).toUpperCase() + type.slice(1).toLowerCase()}: ${term}`;
};

const calculateDateRange = (range) => {
  const now = new Date();
  let start = new Date(now);
  let unit = 'month';
  
  const ranges = {
    '1m': () => { start.setMonth(start.getMonth() - 1); unit = 'day'; },
    '3m': () => { start.setMonth(start.getMonth() - 3); },
    '6m': () => { start.setMonth(start.getMonth() - 6); },
    '1y': () => { start.setFullYear(start.getFullYear() - 1); unit = 'year'; },
    '2y': () => { start.setFullYear(start.getFullYear() - 2); unit = 'year'; },
    '5y': () => { start.setFullYear(start.getFullYear() - 5); unit = 'year'; }
  };
  
  (ranges[range] || ranges['5y'])();
  return { start, end: now, unit };
};

const addOpacity = (color, opacity) => {
  if (!color?.startsWith('#')) return `rgba(255, 255, 255, ${opacity})`;
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

// ============================================================================
// CHART CONFIGURATION
// ============================================================================

const createChartConfig = (isMultiChart = false) => ({
    type: 'line',
  data: { labels: [], datasets: [] },
    options: {
        responsive: true,
    maintainAspectRatio: false,
        devicePixelRatio: window.devicePixelRatio || 1,
    animation: { duration: 800, easing: 'easeOutCubic' },
    interaction: { mode: 'nearest', axis: 'x', intersect: false },
        layout: {
            padding: {
        top: 60, 
        bottom: isMultiChart ? 60 : 340,
        left: 10,
        right: 10
      }
        },
        plugins: {
      legend: { display: false },
      watermark: { opacity: 0.08 },
            tooltip: {
                enabled: true,
        backgroundColor: 'rgba(10, 10, 14, 0.95)',
                titleColor: '#fff',
                bodyColor: '#fff',
        borderColor: 'rgba(255, 255, 255, 0.1)',
                borderWidth: 1,
                cornerRadius: 8,
                padding: 12,
        titleFont: { weight: '600', size: 13 },
        bodyFont: { size: 12 },
                callbacks: {
          title: (items) => {
            if (!items.length) return '';
            const date = new Date(items[0].parsed.x);
            return date.toLocaleDateString('en-US', { 
                                    month: 'short', 
                                    day: 'numeric', 
                                    year: 'numeric' 
                                });
          },
          label: (context) => {
            const label = context.dataset.label?.split(':')[1]?.trim() || context.dataset.label;
            const value = context.parsed.y;
                            const isPrice = context.dataset.yAxisID === 'y1';
            const formatted = isPrice ? value.toFixed(2) : value.toFixed(0);
            return `${label}: ${formatted}`;
                    }
                }
            }
        },
        scales: {
            x: {
                type: 'time',
                time: {
                    unit: 'month',
          displayFormats: { month: 'MMM yyyy', year: 'yyyy' }
                },
                ticks: {
          color: 'rgba(255, 255, 255, 0.5)',
          font: { size: 11 },
          maxRotation: 0
        },
        grid: { display: false },
        border: { display: false }
            },
            y: {
                ticks: {
          color: 'rgba(255, 255, 255, 0.5)',
          font: { size: 11 },
          callback: (value) => value
                },
                grid: { 
          color: 'rgba(255, 255, 255, 0.03)',
                    drawTicks: false
                },
        border: { display: false }
            },
            y1: {
                position: 'right',
                    display: false,
                ticks: {
          color: 'rgba(255, 255, 255, 0.5)',
          font: { size: 11 },
          callback: (value) => value.toFixed(2)
        },
        grid: { display: false },
        border: { display: false }
      }
        }
    }
});

// ============================================================================
// MEMOIZED COMPONENTS
// ============================================================================

const ChartLegend = memo(({ datasets, onToggle, onRemove }) => {
  if (!datasets?.length) return null;

  return (
    <div className="absolute top-4 left-4 right-4 z-50">
      <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin">
        {datasets.map((dataset, index) => {
          const [type, keyword] = dataset.label?.includes(':') 
            ? dataset.label.split(':').map(s => s.trim())
            : ['', dataset.label];
          const isHidden = dataset.hidden;
          const dataType = type.toLowerCase() || 'search';
          const isPrice = dataset.yAxisID === 'y1';
          const icon = isPrice ? DATA_TYPE_ICONS.price : DATA_TYPE_ICONS[dataType] || DATA_TYPE_ICONS.search;
          
          return (
            <div
              key={dataset.label}
              className={`${STYLES.legend.item} ${isHidden ? STYLES.legend.hidden : ''}`}
              onClick={() => onToggle(index)}
            >
              <div 
                className="w-3 h-3 rounded-full flex-shrink-0"
                style={{ backgroundColor: isHidden ? `${dataset.borderColor}40` : dataset.borderColor }}
              />
              {icon && (
                <img 
                  src={icon}
                  alt={dataType}
                  className={`w-4 h-4 ${isHidden ? 'opacity-40' : 'opacity-80'}`}
                />
              )}
              <span className="text-xs font-medium text-white whitespace-nowrap">
                {keyword || dataset.label}
              </span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(index);
                }}
                className="ml-1 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" />
                </svg>
              </button>
        </div>
          );
        })}
      </div>
    </div>
  );
});

const DateRangeSelector = memo(({ current, onChange, analysisHeight, isMultiChart }) => {
  const ranges = ['1m', '3m', '6m', '1y', '2y', '5y'];
  
  // For multi-chart, position at bottom with fixed margin
  // For single chart, position above analysis table
  const bottomPosition = isMultiChart ? '15px' : `${analysisHeight + 10}px`;
  
  return (
    <div 
      className="absolute flex justify-center"
      style={{ 
        bottom: bottomPosition,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 50
      }}
    >
      <div className="flex gap-1 bg-black/60 backdrop-blur-sm rounded-full p-1">
        {ranges.map(range => (
          <button
            key={range}
            onClick={() => onChange(range)}
            className={`px-3 py-1 text-xs font-medium rounded-full transition-all ${
              current === range 
                ? 'bg-white/20 text-white' 
                : 'text-gray-400 hover:text-white hover:bg-white/10'
            }`}
          >
            {range.toUpperCase()}
          </button>
        ))}
      </div>
    </div>
  );
});

const KeywordSelector = memo(({ onAdd, isMultiChart }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [selectedDataType, setSelectedDataType] = useState({ id: 'search', label: 'Search', icon: '/src/assets/icons/google.svg' });
  const searchInputRef = useRef(null);
  const containerRef = useRef(null);

  const dataTypes = useMemo(() => [
    { id: 'search', label: 'Search', icon: '/src/assets/icons/google.svg' },
    { id: 'images', label: 'Images', icon: '/src/assets/icons/image.svg' },
    { id: 'news', label: 'News', icon: '/src/assets/icons/news.svg' },
    { id: 'youtube', label: 'YouTube', icon: '/src/assets/icons/youtube.svg' },
    { id: 'shopping', label: 'Shopping', icon: '/src/assets/icons/shopping.svg' },
    { id: 'tiktok', label: 'TikTok', icon: '/src/assets/icons/tiktok.svg' },
    { id: 'webtraffic', label: 'Web Traffic', icon: '/src/assets/icons/webtraffic.svg' },
    { id: 'wikipedia', label: 'Wikipedia', icon: '/src/assets/icons/wikipedia.svg' },
    { id: 'reddit', label: 'Reddit', icon: '/src/assets/icons/reddit.svg' },
    { id: 'amazon', label: 'Amazon', icon: '/src/assets/icons/amazon.svg' },
    { id: 'price', label: 'Stock Price', icon: '/src/assets/icons/price.svg' }
  ], []);

  // Hover-driven open/close; no click-outside needed

  const handleSearch = useCallback(() => {
    if (keyword.trim()) {
      onAdd(selectedDataType.id, keyword.trim());
      setKeyword('');
      setIsExpanded(false);
    }
  }, [keyword, selectedDataType, onAdd]);

  const selectDataType = useCallback((dataType) => {
    setSelectedDataType(dataType);
  }, []);

  // Position based on chart type
  const topPosition = isMultiChart ? '10px' : '40px';

  return (
    <div className="absolute left-1/2 transform -translate-x-1/2 z-50" style={{ top: topPosition }}>
      <div
        ref={containerRef}
        className="relative flex flex-col items-center group"
        onMouseEnter={() => {
          setIsExpanded(true);
          setTimeout(() => searchInputRef.current?.focus(), 50);
        }}
        onMouseLeave={() => {
          setIsExpanded(false);
          setKeyword('');
        }}
      >
        {/* Trigger icon (always visible) */}
        <button
          className="flex items-center justify-center w-8 h-8 text-gray-400 hover:text-white transition-colors"
          title="Add dataset"
          aria-label="Open search"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {/* Hover panel */}
        {isExpanded && (
          <div className="absolute left-1/2 -translate-x-1/2 mt-2 bg-black rounded-lg p-3 border border-white/10 w-80 shadow-xl">
            {/* Search row */}
            <div className="flex items-center gap-2">
              <button
                className="flex items-center justify-center w-7 h-7 rounded-full hover:bg-white/10 transition-colors"
                title={selectedDataType.label}
              >
                <img src={selectedDataType.icon} alt={selectedDataType.label} className="w-4 h-4 opacity-80" />
              </button>
              <input
                ref={searchInputRef}
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder={`Add ${selectedDataType.label.toLowerCase()} keyword...`}
                className="flex-1 bg-transparent text-sm h-7 focus:outline-none focus:ring-0 border border-white/10 rounded-md px-2 text-gray-200 placeholder-gray-400"
              />
              <button
                className={`flex items-center justify-center w-7 h-7 rounded-md transition-colors border border-white/10 ${keyword ? 'text-gray-200 hover:bg-white/10' : 'text-gray-400'}`}
                onClick={() => keyword && handleSearch()}
                title="Search"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>
            </div>

            {/* Data type selector grid */}
            <div className="mt-3 grid grid-cols-4 gap-2">
              {dataTypes.map((dataType) => (
                <button
                  key={dataType.id}
                  onClick={() => selectDataType(dataType)}
                  className={`flex flex-col items-center gap-1 p-2 rounded-lg transition-all hover:bg-white/10 ${selectedDataType.id === dataType.id ? 'bg-white/10' : ''}`}
                >
                  <img src={dataType.icon} alt={dataType.label} className="w-4 h-4" />
                  <span className="text-[10px] text-gray-300">{dataType.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

const ChartActions = memo(({ chartRef, onAddChart, onReset, isMultiChart }) => {
  const downloadChart = useCallback(() => {
    if (!chartRef?.current) return;
    
    const canvas = chartRef.current.canvas;
    const link = document.createElement('a');
    link.href = canvas.toDataURL('image/png');
    link.download = `chart-${new Date().toISOString().split('T')[0]}.png`;
    link.click();
  }, [chartRef]);
  
  const exportData = useCallback(() => {
    if (!chartRef?.current) return;
    
    const datasets = chartRef.current.data.datasets;
    const dates = new Set();
    
    datasets.forEach(ds => {
      ds.data.forEach(point => {
        dates.add(point.x.toISOString().split('T')[0]);
      });
    });
    
    const sortedDates = Array.from(dates).sort();
    let csv = 'Date,' + datasets.map(ds => ds.label).join(',') + '\n';
    
    sortedDates.forEach(date => {
      const row = [date];
      datasets.forEach(ds => {
        const point = ds.data.find(p => p.x.toISOString().split('T')[0] === date);
        row.push(point ? point.y : '');
      });
      csv += row.join(',') + '\n';
    });
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `data-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  }, [chartRef]);
  
  return (
    <div className="absolute top-4 right-4 z-50 flex gap-2">
      {onAddChart && (
        <button
          onClick={onAddChart}
          className={`${STYLES.button.base} ${STYLES.button.icon}`}
          title="Add Chart"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
          </svg>
        </button>
      )}
      {onReset && (
        <button
          onClick={onReset}
          className={`${STYLES.button.base} ${STYLES.button.icon}`}
          title="Reset"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>
      )}
      {!isMultiChart && (
        <>
          <button
            onClick={downloadChart}
            className={`${STYLES.button.base} ${STYLES.button.icon}`}
            title="Download"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
          </button>
          <button
            onClick={exportData}
            className={`${STYLES.button.base} ${STYLES.button.icon}`}
            title="Export Data"
          >
            <img src={DATA_TYPE_ICONS.excel} alt="Export" className="w-4 h-4 opacity-60" />
          </button>
        </>
      )}
    </div>
  );
});

const AnalysisSummary = memo(({ datasets, expanded, onToggle, analystEstimates = {}, estimatesLoading = {}, onHeightChange }) => {
  const [periodType, setPeriodType] = useState('quarter');
  const [comparisonType, setComparisonType] = useState('yoy');
  const [showAllPeriods, setShowAllPeriods] = useState(true);
  const [tableHeight, setTableHeight] = useState(300);
  const [isDragging, setIsDragging] = useState(false);
  const scrollContainerRef = useRef(null);
  const tableRef = useRef(null);
  const kwHeaderRef = useRef(null);
  const typeHeaderRef = useRef(null);
  const volumeHeaderRef = useRef(null);
  const upHeaderRef = useRef(null);
  // Auto-fit widths and derived sticky offsets for first 4 columns
  const [colWidths, setColWidths] = useState({ keyword: 0, type: 0, volume: 0, up: 0 });
  const [LEFTS, setLEFTS] = useState({ keyword: 0, type: 0, volume: 0, up: 0 });
  const FALLBACK_WIDTHS = useMemo(() => ({ keyword: 120, type: 80, volume: 80, up: 40 }), []);
  const displayWidths = useMemo(() => ({
    keyword: Math.max(colWidths.keyword, FALLBACK_WIDTHS.keyword),
    type: Math.max(colWidths.type, FALLBACK_WIDTHS.type),
    volume: Math.max(colWidths.volume, FALLBACK_WIDTHS.volume),
    up: Math.max(colWidths.up, FALLBACK_WIDTHS.up)
  }), [colWidths, FALLBACK_WIDTHS]);
  const FIRST_FOUR_WIDTH = useMemo(() => (
    displayWidths.keyword + displayWidths.type + displayWidths.volume + displayWidths.up
  ), [displayWidths]);

  // Measure widths after render
  useLayoutEffect(() => {
    const tableEl = tableRef.current;
    if (!tableEl) return;

    const measure = (selector) => {
      const nodes = tableEl.querySelectorAll(selector);
      let max = 0;
      nodes.forEach((el) => {
        max = Math.max(max, el.scrollWidth || 0);
      });
      return Math.ceil(max);
    };

    const keywordW = measure('[data-col="kw"]');
    const typeW = measure('[data-col="type"]');
    const volumeW = measure('[data-col="vol"]');
    const upW = measure('[data-col="up"]');

    // Ensure columns don't overlap: add breathing room and minimums
    const volumeFinal = Math.max(volumeW + 12, 72);
    const upFinal = Math.max(upW + 12, 44);
    setColWidths({
      keyword: keywordW,
      type: typeW,
      volume: volumeFinal,
      up: upFinal
    });
    setLEFTS({
      keyword: 0,
      type: Math.max(keywordW, FALLBACK_WIDTHS.keyword),
      volume: Math.max(keywordW, FALLBACK_WIDTHS.keyword) + Math.max(typeW, FALLBACK_WIDTHS.type),
      up: Math.max(keywordW, FALLBACK_WIDTHS.keyword) + Math.max(typeW, FALLBACK_WIDTHS.type) + Math.max(volumeW, FALLBACK_WIDTHS.volume)
    });
  }, [tableData?.rows, tableData?.periodsToDisplay, periodType, comparisonType, showAllPeriods]);
  
  // Handle dragging
  const handleMouseDown = useCallback((e) => {
    setIsDragging(true);
    e.preventDefault();
  }, []);
  
  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    
    // Calculate new height based on mouse position from window bottom
    const windowHeight = window.innerHeight;
    const newHeight = Math.max(200, Math.min(600, windowHeight - e.clientY));
    setTableHeight(newHeight);
    onHeightChange?.(newHeight);
  }, [isDragging, onHeightChange]);
  
  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);
  
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);
  
  // Initialize height on mount
  useEffect(() => {
    onHeightChange?.(tableHeight);
  }, [onHeightChange, tableHeight]);

  // After render, scroll horizontally to the far right (most recent periods)
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;

    const scrollRight = () => {
      // Run twice to handle late layout/scrollWidth changes
      el.scrollLeft = el.scrollWidth;
      requestAnimationFrame(() => {
        el.scrollLeft = el.scrollWidth;
      });
    };

    // Initial scroll
    requestAnimationFrame(scrollRight);

    // Re-scroll on resize (e.g., when sidebar toggles or window changes)
    const onResize = () => requestAnimationFrame(scrollRight);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [tableData?.periodsToDisplay, tableData?.rows?.length, periodType, comparisonType, showAllPeriods]);

  // Sticky offsets are fixed; no measurement needed
  
  // Calculate periods and data
  const getPeriodStart = (date, type) => {
    const d = new Date(date);
    if (type === 'month') {
      d.setDate(1);
    } else if (type === 'quarter') {
      d.setMonth(Math.floor(d.getMonth() / 3) * 3, 1);
    } else if (type === 'year') {
      d.setMonth(0, 1);
    }
    d.setHours(0, 0, 0, 0);
    return d;
  };
  
    const formatPeriodLabel = (date, type) => {
        if (type === 'month') {
            return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
        } else if (type === 'quarter') {
            const q = Math.floor(date.getMonth() / 3) + 1;
      return `Q${q} ${date.getFullYear()}`;
        } else if (type === 'year') {
            return date.getFullYear();
        }
        return '';
    };

  // Process datasets for table
  const tableData = useMemo(() => {
    if (!datasets || datasets.length === 0) return { periods: [], rows: [] };
    
    // Find all unique periods
        const periodSet = new Set();
        datasets.forEach(dataset => {
            if (!dataset.data) return;
            dataset.data.forEach(point => {
                const periodStart = getPeriodStart(new Date(point.x), periodType).getTime();
                periodSet.add(periodStart);
            });
        });
    
    const periods = Array.from(periodSet).sort((a, b) => a - b).map(ts => new Date(ts));
    const displayPeriods = showAllPeriods ? periods : periods.slice(-4);
    
    // Build rows for each dataset
    const rows = datasets.map(dataset => {
      const [type, keyword] = dataset.label?.includes(':') 
        ? dataset.label.split(':').map(s => s.trim())
        : ['', dataset.label];
      
      // Group data by period
                const periodData = {};
      dataset.data?.forEach(point => {
                    const periodStart = getPeriodStart(new Date(point.x), periodType).getTime();
                    if (!periodData[periodStart]) {
                        periodData[periodStart] = [];
                    }
                    periodData[periodStart].push(point.y);
                });
                
      // Calculate averages
      const periodAverages = {};
                Object.keys(periodData).forEach(periodTs => {
                    const values = periodData[periodTs];
        periodAverages[periodTs] = values.reduce((sum, val) => sum + val, 0) / values.length;
      });
      
      // Calculate changes
      const changes = {};
      displayPeriods.forEach(periodDate => {
            const ts = periodDate.getTime();
        const currentValue = periodAverages[ts];
        
        let compareValue = null;
                        if (comparisonType === 'yoy') {
          const prevYear = new Date(periodDate);
          prevYear.setFullYear(prevYear.getFullYear() - 1);
          compareValue = periodAverages[getPeriodStart(prevYear, periodType).getTime()];
                } else {
          // Find previous period
          const sortedPeriods = Object.keys(periodAverages).map(Number).sort((a, b) => a - b);
          const currentIndex = sortedPeriods.indexOf(ts);
          if (currentIndex > 0) {
            compareValue = periodAverages[sortedPeriods[currentIndex - 1]];
          }
        }
        
        if (currentValue != null && compareValue != null && compareValue !== 0) {
          const change = ((currentValue - compareValue) / Math.abs(compareValue)) * 100;
          changes[ts] = change.toFixed(1);
                } else {
                    changes[ts] = 'N/A';
                }
            });

            return {
        keyword: keyword || dataset.label,
        dataType: type,
                volume: dataset.volume,
        quarterlyUpCount: dataset.quarterlyUpCount,
        totalDataTypes: dataset.totalDataTypes,
                changes,
        color: dataset.borderColor
      };
    });
    
        // Separate alternative data from price data
    const alternativeDataRows = rows.filter(row => row.dataType?.toLowerCase() !== 'price');
    const priceDataRows = rows.filter(row => row.dataType?.toLowerCase() === 'price');
    
    // Calculate volume-weighted average for ALTERNATIVE DATA ONLY
    const alternativeVolumeWeightedAverage = {};
    let alternativeTotalVolume = 0;
    
    // Calculate total volume for alternative data
    alternativeDataRows.forEach(row => {
      if (row.volume && !isNaN(row.volume)) {
        alternativeTotalVolume += row.volume;
      }
    });
    
    // Calculate weighted average for each period (alternative data only)
                        displayPeriods.forEach(periodDate => {
                            const ts = periodDate.getTime();
      let weightedSum = 0;
      let weightSum = 0;
      
      alternativeDataRows.forEach(row => {
        const changeValue = parseFloat(row.changes[ts]);
        if (!isNaN(changeValue) && row.volume && !isNaN(row.volume)) {
          weightedSum += changeValue * row.volume;
          weightSum += row.volume;
        }
      });
      
      if (weightSum > 0) {
        alternativeVolumeWeightedAverage[ts] = (weightedSum / weightSum).toFixed(1);
                                    } else {
        alternativeVolumeWeightedAverage[ts] = 'N/A';
      }
    });
    
                            // Check if we have stock data and calculate analyst metrics
    const hasStockData = rows.some(row => row.dataType?.toLowerCase() === 'price');
    const revenueEstimates = {};
    const revenueActuals = {};
    const epsEstimates = {};
    const epsActuals = {};
    
    if (hasStockData) {
      // For each period, extract revenue and EPS estimates/actuals
                displayPeriods.forEach(periodDate => {
                    const ts = periodDate.getTime();
        let revenueEstSum = 0, revenueActSum = 0;
        let epsEstSum = 0, epsActSum = 0;
        let revenueCount = 0, epsCount = 0;
        
        rows.forEach(row => {
          if (row.dataType?.toLowerCase() === 'price') {
            // Get analyst estimates for this stock
            const symbol = row.keyword;
            const estimates = analystEstimates[symbol];
            
            if (estimates && (estimates.quarters || estimates.data)) {
              // Find estimates for this period
              const periodYear = periodDate.getFullYear();
              const periodQuarter = Math.floor(periodDate.getMonth() / 3) + 1;
              
              // Look for matching estimates by year and quarter
              const estimatesArray = estimates.quarters || estimates.data || [];
              estimatesArray.forEach(estimate => {
                // Parse reporting_quarter if it exists (format: "Q1 2025")
                let matchesQuarter = false;
                
                if (estimate.reporting_quarter) {
                  const match = estimate.reporting_quarter.match(/Q(\d)\s+(\d{4})/);
                  if (match) {
                    const estQuarter = parseInt(match[1]);
                    const estYear = parseInt(match[2]);
                    matchesQuarter = (estYear === periodYear && estQuarter === periodQuarter);
                  }
                }
                
                if (matchesQuarter) {
                  // Log to see what fields are available
                  console.log(`MATCHED: Period ${periodYear} Q${periodQuarter} with ${estimate.reporting_quarter}`);
                  console.log('Estimate data:', estimate);
                  
                  // Extract the correct fields
                  if (estimate.yoy_estimate_revenue_change !== undefined) {
                    revenueEstSum += estimate.yoy_estimate_revenue_change;
                    revenueCount++;
                  }
                  if (estimate.yoy_actual_revenue_change !== undefined) {
                    revenueActSum += estimate.yoy_actual_revenue_change;
                  }
                  if (estimate.yoy_estimate_eps_change !== undefined) {
                    epsEstSum += estimate.yoy_estimate_eps_change;
                    epsCount++;
                  }
                  if (estimate.yoy_actual_eps_change !== undefined) {
                    epsActSum += estimate.yoy_actual_eps_change;
                  }
                }
              });
                        } else {
              // Use mock data for demonstration when real data isn't available
              const year = periodDate.getFullYear();
              const mockData = {
                2020: { revEst: 5.2, revAct: 4.8, epsEst: 8.1, epsAct: 7.5 },
                2021: { revEst: 12.5, revAct: 13.2, epsEst: 15.3, epsAct: 16.1 },
                2022: { revEst: 8.3, revAct: 7.9, epsEst: 10.2, epsAct: 9.8 },
                2023: { revEst: 6.7, revAct: 7.1, epsEst: 9.5, epsAct: 10.2 },
                2024: { revEst: 9.1, revAct: 8.5, epsEst: 11.3, epsAct: 10.8 },
                2025: { revEst: 7.5, revAct: null, epsEst: 9.8, epsAct: null }
              };
              
              if (mockData[year]) {
                if (mockData[year].revEst !== null) {
                  revenueEstSum += mockData[year].revEst;
                  revenueCount++;
                }
                if (mockData[year].revAct !== null) {
                  revenueActSum += mockData[year].revAct;
                }
                if (mockData[year].epsEst !== null) {
                  epsEstSum += mockData[year].epsEst;
                  epsCount++;
                }
                if (mockData[year].epsAct !== null) {
                  epsActSum += mockData[year].epsAct;
                }
              }
            }
          }
        });
        
        // Calculate averages
        revenueEstimates[ts] = revenueCount > 0 ? (revenueEstSum / revenueCount).toFixed(1) : 'N/A';
        revenueActuals[ts] = revenueCount > 0 && revenueActSum !== 0 ? (revenueActSum / revenueCount).toFixed(1) : 'N/A';
        epsEstimates[ts] = epsCount > 0 ? (epsEstSum / epsCount).toFixed(1) : 'N/A';
        epsActuals[ts] = epsCount > 0 && epsActSum !== 0 ? (epsActSum / epsCount).toFixed(1) : 'N/A';
      });
    }
    
    // Filter periods that have actual alternative data (not all N/A)
    const periodsWithAltData = displayPeriods.filter(period => {
      const ts = period.getTime();
      // Check if any alternative data row has a non-N/A value for this period
      return alternativeDataRows.some(row => {
        const value = row.changes[ts];
        return value !== 'N/A' && value !== undefined && value !== null;
            });
        });
    
    // Filter periods that have actual price data (not all N/A)
    const periodsWithPriceData = displayPeriods.filter(period => {
      const ts = period.getTime();
      // Check if any price data row has a non-N/A value for this period
      return priceDataRows.some(row => {
        const value = row.changes[ts];
        return value !== 'N/A' && value !== undefined && value !== null;
      });
    });
    
    // Determine which periods to actually display, based solely on alternative data if present
    let periodsToDisplay;
    if (alternativeDataRows.length > 0) {
      // When any alternative datasets exist, show only periods where at least one alt dataset has data
      periodsToDisplay = periodsWithAltData;
    } else if (priceDataRows.length > 0) {
      // No alternative datasets present – fall back to periods that have price data
      periodsToDisplay = periodsWithPriceData;
    } else {
      // No datasets – default to all detected periods
      periodsToDisplay = displayPeriods;
    }
    
                    return {
      periods: displayPeriods, 
      periodsWithAltData,  // Periods that have actual alternative data
      periodsWithPriceData,  // Periods that have actual price data
      periodsToDisplay,  // The periods to actually show in the table
      rows,
      alternativeDataRows,
      priceDataRows,
      alternativeVolumeWeightedAverage,
      alternativeTotalVolume,
      hasStockData,
      revenueEstimates,
      revenueActuals,
      epsEstimates,
      epsActuals
    };
  }, [datasets, periodType, comparisonType, showAllPeriods, analystEstimates]);
  
  if (!expanded) {
        return (
      <button
        onClick={onToggle}
        className="absolute bottom-4 right-4 p-3 bg-black/80 backdrop-blur-sm rounded-lg hover:bg-white/10 transition-all z-50 border border-white/10 group"
        title="Show Growth Analysis Table"
      >
        <svg className="w-5 h-5 text-gray-400 group-hover:text-white transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      </button>
    );
  }
  
  // Helper for heatmap coloring
  const getHeatmapColor = (value) => {
    const numValue = parseFloat(value);
    if (isNaN(numValue) || value === 'N/A') return 'transparent';
    
    if (numValue > 0) {
      const intensity = Math.min(Math.abs(numValue) / 50, 1);
      const opacity = 0.05 + (intensity * 0.15); // More subtle background
      return `rgba(34, 197, 94, ${opacity})`; // Green
    } else if (numValue < 0) {
      const intensity = Math.min(Math.abs(numValue) / 50, 1);
      const opacity = 0.05 + (intensity * 0.15); // More subtle background
      return `rgba(239, 68, 68, ${opacity})`; // Red
    }
    return 'transparent';
  };
  
  const getTextColor = (value) => {
    const numValue = parseFloat(value);
    if (value === 'N/A') return '#9ca3af'; // gray-400
    if (numValue > 0) {
      if (numValue <= 5) return '#86efac'; // green-300 - lighter for better contrast
      if (numValue <= 15) return '#4ade80'; // green-400
      if (numValue <= 30) return '#22c55e'; // green-500
      return '#16a34a'; // green-600
    } else if (numValue < 0) {
      if (numValue >= -5) return '#fca5a5'; // red-300 - lighter for better contrast
      if (numValue >= -15) return '#f87171'; // red-400
      if (numValue >= -30) return '#ef4444'; // red-500
      return '#dc2626'; // red-600
    }
    return '#d1d5db'; // gray-300
  };
  
    return (
    <div className="absolute bottom-0 left-0 right-0 bg-gray-950/95 backdrop-blur-sm border-t border-white/10 z-40" style={{ height: `${tableHeight}px` }}>
      {/* Draggable Border */}
      <div 
        className={`absolute top-0 left-0 right-0 h-1 cursor-ns-resize hover:bg-white/20 transition-colors ${isDragging ? 'bg-white/30' : ''}`}
        onMouseDown={handleMouseDown}
        style={{ zIndex: 100 }}
      />
      <div className="h-full flex flex-col">
        {/* Controls - Fixed Height */}
        <div className="flex justify-between items-center px-4 py-3 overflow-x-auto">
          <div className="flex-1 min-w-0">
            <h3 className="text-white font-semibold text-sm whitespace-nowrap">
              {comparisonType === 'yoy' ? 'Year-over-Year' : 'Period-over-Period'} Growth Analysis
            </h3>
          </div>
          <div className="flex gap-2 items-center flex-shrink-0">
                                    <select 
                                        value={periodType} 
                                        onChange={(e) => setPeriodType(e.target.value)}
              className="px-2 py-1 bg-black rounded text-xs text-white border border-white/20 hover:bg-gray-900 focus:bg-gray-900 focus:outline-none"
              style={{ backgroundColor: 'black', color: 'white' }}
            >
              <option value="month" style={{ backgroundColor: 'black', color: 'white' }}>Month</option>
              <option value="quarter" style={{ backgroundColor: 'black', color: 'white' }}>Quarter</option>
              <option value="year" style={{ backgroundColor: 'black', color: 'white' }}>Year</option>
                                    </select>
                                    <select 
                                        value={comparisonType} 
                                        onChange={(e) => setComparisonType(e.target.value)}
              className="px-2 py-1 bg-black rounded text-xs text-white border border-white/20 hover:bg-gray-900 focus:bg-gray-900 focus:outline-none"
              style={{ backgroundColor: 'black', color: 'white' }}
                                    >
              <option value="yoy" style={{ backgroundColor: 'black', color: 'white' }}>Year-over-Year</option>
              <option value="pop" style={{ backgroundColor: 'black', color: 'white' }}>Period-over-Period</option>
                                    </select>
                                            <button
              onClick={() => setShowAllPeriods(!showAllPeriods)}
              className="px-3 py-1 bg-black rounded text-xs text-white border border-white/20 hover:bg-gray-900"
                                            >
              {showAllPeriods ? 'Show Less' : 'Show All'}
                                            </button>
            <div className="w-px h-5 bg-white/20 mx-1" />
                                            <button
              onClick={onToggle}
              className="p-1.5 bg-black rounded hover:bg-gray-900 transition-all border border-white/20 group"
              title="Hide Growth Analysis Table"
                                            >
              <svg className="w-4 h-4 text-gray-400 group-hover:text-white transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M6 18L18 6M6 6l12 12" />
              </svg>
                                            </button>
                                        </div>
                                    </div>

        {/* Table Container - vertical scroll only; horizontal handled by right pane */}
        <div ref={scrollContainerRef} className="flex-1 overflow-y-auto" style={{ maxHeight: 'calc(100% - 52px)', overflowX: 'hidden' }}>
          {tableData.rows.length === 0 ? (
            <div className="text-center text-gray-400 mt-20">
              <p className="text-sm">Add datasets to see performance metrics</p>
                            </div>
                        ) : (
            <div className="grid" style={{ gridTemplateColumns: `${FIRST_FOUR_WIDTH + 2}px 1fr` }}>
              {/* LEFT FIXED TABLE */}
              <div className="sticky left-0 bg-black" style={{ width: FIRST_FOUR_WIDTH + 2, minWidth: FIRST_FOUR_WIDTH + 2, maxWidth: FIRST_FOUR_WIDTH + 2, zIndex: 60, boxShadow: '6px 0 12px rgba(0,0,0,0.6), inset -1px 0 0 rgba(255,255,255,0.06)', overflow: 'hidden', position: 'relative' }}>
                <table ref={tableRef} className="w-full text-xs border-separate" style={{ borderSpacing: 0, tableLayout: 'fixed' }}>
                  <colgroup>
                    <col style={{ width: displayWidths.keyword }} />
                    <col style={{ width: displayWidths.type }} />
                    <col style={{ width: displayWidths.volume }} />
                    <col style={{ width: displayWidths.up }} />
                  </colgroup>
                  <thead className="sticky top-0 bg-black" style={{ zIndex: 70 }}>
                    <tr>
                      <th data-col="kw" className="text-left py-2 px-2 text-gray-400 font-medium whitespace-nowrap" style={{ width: displayWidths.keyword, minWidth: displayWidths.keyword, maxWidth: displayWidths.keyword }}>Keyword</th>
                      <th data-col="type" className="text-left py-2 px-2 text-gray-400 font-medium whitespace-nowrap" style={{ width: displayWidths.type, minWidth: displayWidths.type, maxWidth: displayWidths.type }}>Type</th>
                      <th data-col="vol" className="text-right py-2 px-2 text-gray-400 font-medium whitespace-nowrap" style={{ width: displayWidths.volume, minWidth: displayWidths.volume, maxWidth: displayWidths.volume, borderRight: '1px solid rgba(255,255,255,0.06)'}}>Volume</th>
                      <th data-col="up" className="text-center py-2 px-2 text-gray-400 font-medium whitespace-nowrap" style={{ width: displayWidths.up, minWidth: displayWidths.up, maxWidth: displayWidths.up}}>↑</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableData.alternativeDataRows.length > 0 && tableData.alternativeTotalVolume > 0 && (
                      <tr className="bg-black">
                        <td className="py-2 px-2 text-white font-bold" colSpan={4}>
                          <div className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full flex-shrink-0 bg-green-400" />
                            <span>Volume-Weighted Average</span>
                          </div>
                        </td>
                      </tr>
                    )}
                    {tableData.alternativeDataRows.map((row, idx) => {
                      const dataTypeIcon = DATA_TYPE_ICONS[row.dataType?.toLowerCase()] || DATA_TYPE_ICONS.search;
                      return (
                        <tr key={`alt-left-${idx}`} className="bg-black">
                          <td data-col="kw" className="py-2 px-2 text-white whitespace-nowrap" style={{ width: displayWidths.keyword, minWidth: displayWidths.keyword, maxWidth: displayWidths.keyword }}>
                            <div className="flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: row.color }} />
                              <span>{row.keyword}</span>
                            </div>
                          </td>
                          <td data-col="type" className="py-2 px-2 text-gray-300 whitespace-nowrap" style={{ width: displayWidths.type, minWidth: displayWidths.type, maxWidth: displayWidths.type }}>
                            <div className="flex items-center gap-1">
                              {dataTypeIcon && (
                                <img src={dataTypeIcon} alt={row.dataType} className="w-3 h-3 opacity-60" />
                              )}
                              <span className="capitalize">{row.dataType}</span>
                            </div>
                          </td>
                          <td data-col="vol" className="py-2 px-2 text-right text-gray-300 whitespace-nowrap" style={{ width: displayWidths.volume, minWidth: displayWidths.volume, maxWidth: displayWidths.volume, borderRight: '1px solid rgba(255,255,255,0.06)' }}>
                            {row.volume ? (row.volume >= 1000000 ? `${(row.volume/1000000).toFixed(1)}M` : row.volume >= 1000 ? `${(row.volume/1000).toFixed(0)}K` : row.volume) : '-'}
                          </td>
                          <td data-col="up" className="py-2 px-2 text-center text-gray-300 whitespace-nowrap" style={{ width: displayWidths.up, minWidth: displayWidths.up, maxWidth: displayWidths.up, boxShadow: 'inset -2px 0 0 #000' }}>
                            {row.quarterlyUpCount !== undefined ? `${row.quarterlyUpCount}/${row.totalDataTypes}` : '-'}
                          </td>
                        </tr>
                      );
                    })}
                    {tableData.alternativeDataRows.length > 0 && tableData.priceDataRows.length > 0 && (
                      <tr><td colSpan={4} className="py-1 bg-black"></td></tr>
                    )}
                    {tableData.priceDataRows.map((row, idx) => {
                      const dataTypeIcon = DATA_TYPE_ICONS[row.dataType?.toLowerCase()] || DATA_TYPE_ICONS.price;
                      return (
                        <tr key={`price-left-${idx}`} className="bg-black">
                          <td className="py-2 px-2 text-white whitespace-nowrap" style={{ width: displayWidths.keyword, minWidth: displayWidths.keyword, maxWidth: displayWidths.keyword }}>
                            <div className="flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: row.color }} />
                              <span>{row.keyword}</span>
                            </div>
                          </td>
                          <td className="py-2 px-2 text-gray-300 whitespace-nowrap" style={{ width: displayWidths.type, minWidth: displayWidths.type, maxWidth: displayWidths.type }}>
                            <div className="flex items-center gap-1">
                              {dataTypeIcon && (
                                <img src={dataTypeIcon} alt={row.dataType} className="w-3 h-3 opacity-60" />
                              )}
                              <span className="capitalize">{row.dataType}</span>
                            </div>
                          </td>
                          <td className="py-2 px-2 text-right text-gray-300 whitespace-nowrap" style={{ width: displayWidths.volume, minWidth: displayWidths.volume, maxWidth: displayWidths.volume, borderRight: '1px solid rgba(255,255,255,0.06)' }}>
                            {row.volume ? (row.volume >= 1000000 ? `${(row.volume/1000000).toFixed(1)}M` : row.volume >= 1000 ? `${(row.volume/1000).toFixed(0)}K` : row.volume) : '-'}
                          </td>
                          <td className="py-2 px-2 text-center text-gray-300 whitespace-nowrap" style={{ width: displayWidths.up, minWidth: displayWidths.up, maxWidth: displayWidths.up, boxShadow: 'inset -2px 0 0 #000' }}>
                            {row.quarterlyUpCount !== undefined ? `${row.quarterlyUpCount}/${row.totalDataTypes}` : '-'}
                          </td>
                        </tr>
                      );
                    })}
                    {tableData.priceDataRows.length > 0 && (
                      <>
                        <tr className="bg-black"><td colSpan={4} className="py-1 px-2 text-white font-semibold text-sm">Analyst Consensus Metrics</td></tr>
                        <tr className="bg-black">
                          <td className="py-2 px-2 text-white font-semibold"><div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full flex-shrink-0 bg-yellow-500" /><span>Revenue Δ</span></div></td>
                          <td className="py-2 px-2 text-gray-300"><span className="text-xs italic">Estimates</span></td>
                          <td className="py-2 px-2 text-right text-gray-300">-</td>
                          <td className="py-2 px-2 text-center text-gray-300">-</td>
                        </tr>
                        <tr className="bg-black">
                          <td className="py-2 px-2 text-white font-semibold"><div className="flex items-center gap-2"><span className="w-2 h-2"></span><span className="pl-4">→</span></div></td>
                          <td className="py-2 px-2 text-gray-300"><span className="text-xs italic">Actuals</span></td>
                          <td className="py-2 px-2 text-right text-gray-300">-</td>
                          <td className="py-2 px-2 text-center text-gray-300">-</td>
                        </tr>
                        <tr className="bg-black">
                          <td className="py-2 px-2 text-white font-semibold"><div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full flex-shrink-0 bg-blue-500" /><span>EPS Δ</span></div></td>
                          <td className="py-2 px-2 text-gray-300"><span className="text-xs italic">Estimates</span></td>
                          <td className="py-2 px-2 text-right text-gray-300">-</td>
                          <td className="py-2 px-2 text-center text-gray-300">-</td>
                        </tr>
                        <tr className="bg-black">
                          <td className="py-2 px-2 text-white font-semibold"><div className="flex items-center gap-2"><span className="w-2 h-2"></span><span className="pl-4">→</span></div></td>
                          <td className="py-2 px-2 text-gray-300"><span className="text-xs italic">Actuals</span></td>
                          <td className="py-2 px-2 text-right text-gray-300">-</td>
                          <td className="py-2 px-2 text-center text-gray-300">-</td>
                        </tr>
                      </>
                    )}
                  </tbody>
                </table>
              </div>

              {/* RIGHT SCROLLABLE TABLE */}
              <div className="overflow-x-auto" style={{ position: 'relative', zIndex: 10 }}>
                <table className="w-full text-xs border-separate" style={{ borderSpacing: 0 }}>
                  <thead className="sticky top-0 bg-black" style={{ zIndex: 20 }}>
                    <tr>
                      {tableData.periodsToDisplay.map(period => (
                        <th key={period.getTime()} className="text-right py-2 px-2 text-gray-400 font-medium whitespace-nowrap">
                          {formatPeriodLabel(period, periodType)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {tableData.alternativeDataRows.length > 0 && tableData.alternativeTotalVolume > 0 && (
                      <tr>
                        {tableData.periodsToDisplay.map(period => {
                          const value = tableData.alternativeVolumeWeightedAverage[period.getTime()];
                          const numValue = parseFloat(value);
                          const bgColor = getHeatmapColor(value);
                          const textColor = getTextColor(value);
                          return (
                            <td key={period.getTime()} className="py-2 px-2 text-right font-bold whitespace-nowrap" style={{ backgroundColor: bgColor, color: textColor }}>
                              {value !== 'N/A' && numValue > 0 ? '+' : ''}{value}{value !== 'N/A' ? '%' : ''}
                            </td>
                          );
                        })}
                      </tr>
                    )}

                    {tableData.alternativeDataRows.map((row, idx) => {
                      const isEvenRow = idx % 2 === 0;
                      return (
                        <tr key={`alt-right-${idx}`} className={`${isEvenRow ? 'bg-gray-900/30' : 'bg-transparent'} hover:bg-white/5 transition-colors`}>
                          {tableData.periodsToDisplay.map(period => {
                            const value = row.changes[period.getTime()];
                            const numValue = parseFloat(value);
                            const bgColor = getHeatmapColor(value);
                            const textColor = getTextColor(value);
                            return (
                              <td key={period.getTime()} className="py-2 px-2 text-right font-medium whitespace-nowrap" style={{ backgroundColor: bgColor, color: textColor }}>
                                {value !== 'N/A' && numValue > 0 ? '+' : ''}{value}{value !== 'N/A' ? '%' : ''}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}

                    {tableData.alternativeDataRows.length > 0 && tableData.priceDataRows.length > 0 && (
                      <tr>
                        <td colSpan={tableData.periodsToDisplay.length} className="py-1 bg-black"></td>
                      </tr>
                    )}

                    {tableData.priceDataRows.map((row, idx) => {
                      const isEvenRow = idx % 2 === 0;
                      return (
                        <tr key={`price-right-${idx}`} className={`${isEvenRow ? 'bg-gray-900/30' : 'bg-transparent'} hover:bg-white/5 transition-colors`}>
                          {tableData.periodsToDisplay.map(period => {
                            const value = row.changes[period.getTime()];
                            const numValue = parseFloat(value);
                            const bgColor = getHeatmapColor(value);
                            const textColor = getTextColor(value);
                            return (
                              <td key={period.getTime()} className="py-2 px-2 text-right font-medium whitespace-nowrap" style={{ backgroundColor: bgColor, color: textColor }}>
                                {value !== 'N/A' && numValue > 0 ? '+' : ''}{value}{value !== 'N/A' ? '%' : ''}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}

                    {tableData.priceDataRows.length > 0 && (
                      <>
                        <tr>
                          {tableData.periodsToDisplay.map(() => (
                            <td key={Math.random()} className="py-1 bg-gray-800/30"></td>
                          ))}
                        </tr>
                        <tr className="bg-gray-800/30">
                          {tableData.periodsToDisplay.map(period => {
                            const value = tableData.revenueEstimates?.[period.getTime()];
                            if (value && value !== 'N/A') {
                              const numValue = parseFloat(value);
                              const bgColor = getHeatmapColor(value);
                              const textColor = getTextColor(value);
                              return (
                                <td key={`rev-est-${period.getTime()}`} className="py-2 px-2 text-right font-semibold whitespace-nowrap" style={{ backgroundColor: bgColor, color: textColor }}>
                                  {numValue > 0 ? '+' : ''}{value}%
                                </td>
                              );
                            }
                            return <td key={`rev-est-${period.getTime()}`} className="py-2 px-2 text-right text-gray-500 whitespace-nowrap">-</td>;
                          })}
                        </tr>
                        <tr className="bg-gray-800/30">
                          {tableData.periodsToDisplay.map(period => {
                            const value = tableData.revenueActuals?.[period.getTime()];
                            const estimate = parseFloat(tableData.revenueEstimates?.[period.getTime()]);
                            if (value && value !== 'N/A') {
                              const numValue = parseFloat(value);
                              let bgColor = getHeatmapColor(value);
                              let textColor = getTextColor(value);
                              if (!isNaN(estimate)) {
                                const delta = numValue - estimate;
                                if (delta > 1) bgColor = 'rgba(34, 197, 94, 0.3)';
                                else if (delta < -1) bgColor = 'rgba(239, 68, 68, 0.3)';
                              }
                              return (
                                <td key={`rev-act-${period.getTime()}`} className="py-2 px-2 text-right font-bold whitespace-nowrap" style={{ backgroundColor: bgColor, color: textColor }}>
                                  {numValue > 0 ? '+' : ''}{value}%
                                </td>
                              );
                            }
                            return <td key={`rev-act-${period.getTime()}`} className="py-2 px-2 text-right text-gray-500 whitespace-nowrap">TBD</td>;
                          })}
                        </tr>
                        <tr className="bg-gray-800/30">
                          {tableData.periodsToDisplay.map(period => {
                            const value = tableData.epsEstimates?.[period.getTime()];
                            if (value && value !== 'N/A') {
                              const numValue = parseFloat(value);
                              const bgColor = getHeatmapColor(value);
                              const textColor = getTextColor(value);
                              return (
                                <td key={`eps-est-${period.getTime()}`} className="py-2 px-2 text-right font-semibold whitespace-nowrap" style={{ backgroundColor: bgColor, color: textColor }}>
                                  {numValue > 0 ? '+' : ''}{value}%
                                </td>
                              );
                            }
                            return <td key={`eps-est-${period.getTime()}`} className="py-2 px-2 text-right text-gray-500 whitespace-nowrap">-</td>;
                          })}
                        </tr>
                        <tr className="bg-gray-800/30">
                          {tableData.periodsToDisplay.map(period => {
                            const value = tableData.epsActuals?.[period.getTime()];
                            const estimate = parseFloat(tableData.epsEstimates?.[period.getTime()]);
                            if (value && value !== 'N/A') {
                              const numValue = parseFloat(value);
                              let bgColor = getHeatmapColor(value);
                              let textColor = getTextColor(value);
                              if (!isNaN(estimate)) {
                                const delta = numValue - estimate;
                                if (delta > 1) bgColor = 'rgba(34, 197, 94, 0.3)';
                                else if (delta < -1) bgColor = 'rgba(239, 68, 68, 0.3)';
                              }
                              return (
                                <td key={`eps-act-${period.getTime()}`} className="py-2 px-2 text-right font-bold whitespace-nowrap" style={{ backgroundColor: bgColor, color: textColor }}>
                                  {numValue > 0 ? '+' : ''}{value}%
                                </td>
                              );
                            }
                            return <td key={`eps-act-${period.getTime()}`} className="py-2 px-2 text-right text-gray-500 whitespace-nowrap">TBD</td>;
                          })}
                        </tr>
                      </>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
                    )}
                    </div>
                </div>
            </div>
    );
});

// ============================================================================
// LOADER COMPONENT
// ============================================================================

const ParadoxLoader = React.memo(function ParadoxLoader({ isLoading }) {
  return isLoading ? (
    <div className="absolute inset-0 z-[1000] flex items-center justify-center pointer-events-none">
      <img 
        src="/src/assets/images/PARADOX-transparant-white.svg" 
        alt="Loading..." 
        style={{
          width: '64px',
          height: '64px',
          animation: 'loadingAnimation 2s ease-in-out infinite'
        }}
      />
            </div>
  ) : null;
});

// Add keyframes definition for the loading animation
if (typeof document !== 'undefined' && !document.getElementById('trend-chart-keyframes')) {
  const styleSheet = document.createElement('style');
  styleSheet.id = 'trend-chart-keyframes';
  styleSheet.textContent = `
    @keyframes loadingAnimation {
      0%    { filter: brightness(25%); }
      50%   { filter: brightness(100%); }
      100%  { filter: brightness(25%); }
    }`;
  document.head.appendChild(styleSheet);
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const TrendChart = React.forwardRef(({ 
  id = 'chart', 
  initialDatasets = [], 
  onDatasetsChange, 
  onAddChart, 
  onReset, 
  isMultiChart = false 
}, ref) => {
  // State Management
    const canvasRef = useRef(null);
    const chartRef = useRef(null);
    const [datasets, setDatasets] = useState(initialDatasets);
  const [dateRange, setDateRange] = useState('5y');
  const [loadingStates, setLoadingStates] = useState({}); // Track loading for each dataset
  const [error, setError] = useState(null);
  const [errorContext, setErrorContext] = useState(null); // Store context about what failed
  const [analysisExpanded, setAnalysisExpanded] = useState(true); // Show analysis by default
  const [analysisHeight, setAnalysisHeight] = useState(300);
    
    // Analyst Estimates State
  const [analystEstimates, setAnalystEstimates] = useState({});
  const [estimatesLoading, setEstimatesLoading] = useState({});
  
  // Helper: Recalculate dynamic Y scale (alternative data axis) based on visible data within date range
  const recalcYScale = useCallback(() => {
    const chart = chartRef.current;
    if (!chart) return;

    const xMinStr = chart.options?.scales?.x?.min;
    const xMaxStr = chart.options?.scales?.x?.max;
    const xMin = xMinStr ? new Date(xMinStr).getTime() : -Infinity;
    const xMax = xMaxStr ? new Date(xMaxStr).getTime() : Infinity;

    let minY = Infinity;
    let maxY = -Infinity;

    chart.data.datasets.forEach((ds, index) => {
      // Only consider alternative data axis (y), skip price axis (y1)
      if (ds?.yAxisID === 'y1') return;

      const meta = chart.getDatasetMeta(index);
      if (meta?.hidden) return;

      (ds.data || []).forEach(point => {
        if (!point) return;
        const t = point.x instanceof Date ? point.x.getTime() : new Date(point.x).getTime();
        if (Number.isFinite(t) && t >= xMin && t <= xMax) {
          const yVal = point.y;
          if (typeof yVal === 'number' && Number.isFinite(yVal)) {
            if (yVal < minY) minY = yVal;
            if (yVal > maxY) maxY = yVal;
          }
        }
      });
    });

    if (minY !== Infinity && maxY !== -Infinity) {
      chart.options.scales.y.min = minY;
      chart.options.scales.y.max = maxY;
      // Ensure stepSize is not forced when real data is present
      if (chart.options.scales.y.ticks) {
        delete chart.options.scales.y.ticks.stepSize;
      }
    } else {
      const hasAnyDatasets = Array.isArray(chart.data?.datasets) && chart.data.datasets.length > 0;
      if (hasAnyDatasets) {
        // Let Chart.js auto-scale when there are datasets (e.g., only price data)
        delete chart.options.scales.y.min;
        delete chart.options.scales.y.max;
        if (chart.options.scales.y.ticks) {
          delete chart.options.scales.y.ticks.stepSize;
        }
      } else {
        // No datasets loaded yet — show placeholder range 0–100 with ticks 0, 50, 100
        chart.options.scales.y.min = 0;
        chart.options.scales.y.max = 100;
        if (chart.options.scales.y.ticks) {
          chart.options.scales.y.ticks.stepSize = 50;
        }
      }
    }

    chart.update('none');
  }, []);

  // Fetch Analyst Estimates
  const fetchAnalystEstimates = useCallback(async (symbol) => {
    if (!symbol || estimatesLoading[symbol] || analystEstimates[symbol]) return;
    
    setEstimatesLoading(prev => ({ ...prev, [symbol]: true }));
    
    try {
      const response = await fetch(`/analyst_expectations?symbol=${encodeURIComponent(symbol)}&scope=historical`);
      if (response.ok) {
        const data = await response.json();
        console.log(`Analyst estimates for ${symbol}:`, data);
        console.log(`Data structure - has quarters: ${!!data.quarters}, has data: ${!!data.data}`);
        setAnalystEstimates(prev => ({ ...prev, [symbol]: data }));
      }
    } catch (error) {
      console.warn('Error fetching analyst estimates:', error);
    } finally {
      setEstimatesLoading(prev => ({ ...prev, [symbol]: false }));
    }
  }, [estimatesLoading, analystEstimates]);
  
  // Initialize Chart
    useEffect(() => {
    if (!canvasRef.current) return;
    
    const ctx = canvasRef.current.getContext('2d');
    const config = createChartConfig(isMultiChart);
    chartRef.current = new Chart(ctx, config);
    
    // Set initial date range
    const { start, end, unit } = calculateDateRange(dateRange);
    chartRef.current.options.scales.x.time.unit = unit;
    chartRef.current.options.scales.x.min = start.toISOString();
    chartRef.current.options.scales.x.max = end.toISOString();
    recalcYScale();

        return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
      }
    };
    }, [isMultiChart, recalcYScale]);

  // Fetch Data with all original functionality
  const fetchData = useCallback(async (dataType, keyword) => {
    const label = formatDatasetLabel(dataType, keyword);
    
    // Check if already exists and toggle/remove if so
    const existingIndex = chartRef.current?.data.datasets.findIndex(d => d.label === label);
    if (existingIndex !== -1) {
      chartRef.current.data.datasets.splice(existingIndex, 1);
      chartRef.current.update();
      const newDatasets = chartRef.current.data.datasets;
      setDatasets([...newDatasets]);
      onDatasetsChange?.(newDatasets);
      return;
    }
    
    // Create a unique key for this dataset loading state
    const loadingKey = `${dataType}_${keyword}`;
    
    // Prevent duplicate loading
    if (loadingStates[loadingKey]) {
      return;
    }
    
    setLoadingStates(prev => ({ ...prev, [loadingKey]: true }));
    setError(null);
    setErrorContext(null);
    
    try {
      const url = dataType === 'price' 
        ? `/data?type=price&source=price&keyword=${encodeURIComponent(keyword)}`
        : `/data?type=${dataType}&keyword=${encodeURIComponent(keyword)}`;
        
      const response = await fetch(url);
            
            if (response.status === 401) {
                window.location.href = '/login';
        return;
            }
            
            if (!response.ok) {
                const errorText = await response.text();
                try {
                    const errorData = JSON.parse(errorText);
                    if (errorData.type === 'auth_expired') {
                        window.location.href = '/login';
            return;
                    }
          throw new Error(errorData.error || `No data available: ${dataType} data for "${keyword}"`);
                } catch (e) {
          throw new Error(`No data available: ${dataType} data for "${keyword}"`);
                }
            }

            const result = await response.json();

            if (!result.data || !result.labels || result.data.length === 0) {
                throw new Error(`No ${dataType} data available for "${keyword}"`);
            }

      // Process data points
            const dataPoints = result.data.map((value, index) => {
        const dateStr = result.labels[index];
        if (!dateStr) return null;
        
        const dateParts = dateStr.split('-');
        if (dateParts.length !== 3) return null;

                const dateValue = new Date(
          parseInt(dateParts[0]),
          parseInt(dateParts[1]) - 1,
          parseInt(dateParts[2])
                );
                
                const parsedValue = parseFloat(value);
        if (isNaN(parsedValue)) return null;
        
        return { x: dateValue, y: parsedValue };
      }).filter(point => point && !isNaN(point.x.getTime())).sort((a, b) => a.x - b.x);

            if (dataPoints.length === 0) {
        throw new Error(`No valid ${dataType} data points found for "${keyword}"`);
            }

      // Process volume data if available
            let volumeDataPoints = null;
      let latestVolumeValue = 0;
            if (result.volume && result.volume.length === result.data.length) {
                volumeDataPoints = result.volume.map((value, index) => {
          const dateStr = result.labels[index];
          if (!dateStr) return null;
          const dateParts = dateStr.split('-');
          if (dateParts.length !== 3) return null;

                    const dateValue = new Date(
            parseInt(dateParts[0]),
            parseInt(dateParts[1]) - 1,
            parseInt(dateParts[2])
                    );
                    
                    const parsedValue = parseFloat(value);
          if (isNaN(parsedValue)) return null;
          
          return { x: dateValue, y: parsedValue };
        }).filter(point => point && !isNaN(point.x.getTime())).sort((a, b) => a.x - b.x);
        
                if (volumeDataPoints.length > 0) {
                    latestVolumeValue = volumeDataPoints[volumeDataPoints.length - 1].y;
        }
      }
      
      // Fetch volume for keyword (fallback)
      let volume = latestVolumeValue;
      if (volume === 0 && dataType === 'search') {
        try {
          const volumeResponse = await fetch(`/keyword_volume?keyword=${encodeURIComponent(keyword)}`);
                    if (volumeResponse.ok) {
                        const volumeData = await volumeResponse.json();
                        volume = volumeData.volume || 0;
                    }
                } catch (error) {
          console.warn('Error fetching volume:', error);
                }
            }

      // Fetch quarterly metadata (only for actual keywords, not company symbols)
            let quarterlyUpCount = 0;
            let totalDataTypes = 0;
      if (dataType !== 'price') {
        // Skip metadata fetch for company symbols (typically 1-5 uppercase letters, possibly with dots)
        const isLikelyCompanySymbol = /^[A-Z]{1,5}(\.[A-Z]{1,2})?$/.test(keyword);
        
        if (!isLikelyCompanySymbol) {
          try {
            const metadataResponse = await fetch(`/keyword_metadata?keyword=${encodeURIComponent(keyword)}`);
                      if (metadataResponse.ok) {
                          const metadataData = await metadataResponse.json();
                          quarterlyUpCount = metadataData.quarterly_up_count || 0;
                          totalDataTypes = metadataData.total_data_types || 0;
                      }
                  } catch (error) {
            console.warn('Error fetching metadata:', error);
          }
        } else {
          console.log(`Skipping metadata fetch for company symbol: ${keyword}`);
        }
      }
      
      // Create dataset with all metadata
      const colorIndex = (chartRef.current?.data.datasets.length || 0) % COLORS.length;
      const color = COLORS[colorIndex];
      const isPrice = dataType === 'price';
      
            const newDataset = {
        label,
        data: dataPoints,
                borderColor: color,
        backgroundColor: addOpacity(color, 0.08),
        borderWidth: isPrice ? 3 : 2,
                tension: 0.4,
                pointRadius: 0,
                pointHoverRadius: 6,
                pointHoverBackgroundColor: color,
        pointHoverBorderColor: '#fff',
        pointHoverBorderWidth: 2,
        yAxisID: isPrice ? 'y1' : 'y',
        fill: true,
        hidden: false,
        // Additional metadata
        volume: volume,
        volumeData: volumeDataPoints,
        hasVolumeData: !!volumeDataPoints,
        quarterlyUpCount: quarterlyUpCount,
        totalDataTypes: totalDataTypes
      };
      
      // Update chart
      if (chartRef.current) {
        chartRef.current.data.datasets.push(newDataset);
        
            if (isPrice) {
          const prices = dataPoints.map(p => p.y);
          const min = Math.min(...prices);
          const max = Math.max(...prices);
          const padding = (max - min) * 0.1;
          
          chartRef.current.options.scales.y1.display = true;
          chartRef.current.options.scales.y1.min = Math.max(0, min - padding);
          chartRef.current.options.scales.y1.max = max + padding;
          
          // Fetch analyst estimates for stock symbols
          fetchAnalystEstimates(keyword);
        }
        
        recalcYScale();
        
        // Update state
        const newDatasets = [...chartRef.current.data.datasets];
        setDatasets(newDatasets);
        onDatasetsChange?.(newDatasets);
      }
      
    } catch (err) {
      console.error('Error fetching data:', err);
      const errorMessage = err.message || `Failed to load ${dataType} data for "${keyword}"`;
      setError(errorMessage);
      setErrorContext({ dataType, keyword, timestamp: Date.now() });
      setTimeout(() => {
        setError(null);
        setErrorContext(null);
      }, 5000);
        } finally {
      setLoadingStates(prev => ({ ...prev, [loadingKey]: false }));
        }
  }, [onDatasetsChange, fetchAnalystEstimates]);

  // Handle Legend Actions
  const handleToggleDataset = useCallback((index) => {
        if (!chartRef.current) return;
    
    const meta = chartRef.current.getDatasetMeta(index);
    meta.hidden = !meta.hidden;
    recalcYScale();
    
    const newDatasets = [...datasets];
    newDatasets[index].hidden = meta.hidden;
    setDatasets(newDatasets);
    onDatasetsChange?.(newDatasets);
  }, [datasets, onDatasetsChange, recalcYScale]);
  
  const handleRemoveDataset = useCallback((index) => {
        if (!chartRef.current) return;
        
    chartRef.current.data.datasets.splice(index, 1);
    recalcYScale();
    
    const newDatasets = datasets.filter((_, i) => i !== index);
    setDatasets(newDatasets);
    onDatasetsChange?.(newDatasets);
    
    // Hide price axis if no price datasets
    if (!newDatasets.some(d => d.yAxisID === 'y1')) {
                chartRef.current.options.scales.y1.display = false;
            chartRef.current.update('none');
    }
  }, [datasets, onDatasetsChange, recalcYScale]);
  
  // Handle Date Range Change
  const handleDateRangeChange = useCallback((range) => {
    setDateRange(range);
        if (!chartRef.current) return;
        
    const { start, end, unit } = calculateDateRange(range);
    chartRef.current.options.scales.x.time.unit = unit;
    chartRef.current.options.scales.x.min = start.toISOString();
    chartRef.current.options.scales.x.max = end.toISOString();
                recalcYScale();
    }, [recalcYScale]);

  // Update chart padding when analysis expands or height changes
    useEffect(() => {
        if (!chartRef.current) return;
        
    const padding = isMultiChart ? 60 : (analysisExpanded ? analysisHeight + 40 : 60);
    chartRef.current.options.layout.padding.bottom = padding;
    chartRef.current.update('none');
  }, [analysisExpanded, isMultiChart, analysisHeight]);
  
  // Expose methods via ref
  React.useImperativeHandle(ref, () => ({
    handleDataTypeSelect: fetchData, // For compatibility with TrendChartGrid
    addDataset: fetchData,
    getChart: () => chartRef.current
  }), [fetchData]);
  
  // Calculate if any loading is happening
  const isAnyLoading = Object.values(loadingStates).some(v => v) || Object.values(estimatesLoading).some(v => v);

    return (
    <div className="relative h-full w-full overflow-hidden" style={STYLES.container}>
      {/* Loading Indicator */}
            <ParadoxLoader isLoading={isAnyLoading} />
      
      {/* Error Message */}
      {error && (
        <div className="absolute left-4 right-4 z-[9999] max-w-sm mx-auto" style={{top: '96px', position: 'absolute', zIndex: 9999}}>
          <div className="bg-black border border-red-500/50 rounded-lg p-3" style={{backgroundColor: '#000000', position: 'relative'}}>
            <div className="flex items-center gap-2">
              <div className="flex-shrink-0 w-4 h-4">
                <svg className="w-4 h-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-xs font-medium">{error}</p>
              </div>
              <button
                onClick={() => {
                  setError(null);
                  setErrorContext(null);
                }}
                className="flex-shrink-0 text-gray-400 hover:text-white transition-colors"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Chart Canvas */}
      <canvas ref={canvasRef} className="w-full h-full" />
      
      {/* UI Components */}
      <ChartLegend
        datasets={datasets}
        onToggle={handleToggleDataset}
        onRemove={handleRemoveDataset}
      />
      
      <KeywordSelector onAdd={fetchData} isMultiChart={isMultiChart} />
      
      <ChartActions
                        chartRef={chartRef} 
                        onAddChart={onAddChart} 
                        onReset={onReset} 
                        isMultiChart={isMultiChart} 
      />
      
      <DateRangeSelector
        current={dateRange}
        onChange={handleDateRangeChange}
        analysisHeight={analysisExpanded ? analysisHeight : 60}
        isMultiChart={isMultiChart}
      />
      
                {!isMultiChart && (
        <AnalysisSummary
                        datasets={datasets} 
          expanded={analysisExpanded}
          onToggle={() => setAnalysisExpanded(!analysisExpanded)}
          analystEstimates={analystEstimates}
          estimatesLoading={estimatesLoading}
          onHeightChange={setAnalysisHeight}
        />
      )}
        </div>
    );
});

TrendChart.displayName = 'TrendChart';

export default TrendChart; 